<template>
	<BaseDialog v-model="dialogVisible" size="md">
		<template #title="{ titleId }">
			<h5 :id="titleId" data-test-id="dialog-title" class="exchange-auth-health-title">
				<span class="d-none d-sm-inline">Some of your Apps need to be reconnected</span>
				<span class="d-sm-none">Apps need reconnection</span>
			</h5>
		</template>
		<div v-if="canReconnect" class="mb-3">Please click "Log in" to reconnect your App.</div>
		<div v-else class="mb-3">Please contact your organization's administrator to reconnect these Apps.</div>
		<div v-for="platform in platforms" :key="platform.platformId" class="mb-3">
			<v-card variant="outlined" rounded="lg">
				<v-card-text class="pa-3">
					<div class="d-flex align-center justify-space-between exchange-auth-platform-row">
						<div class="flex-grow-1">
							<div class="text-subtitle-1 font-weight-bold mb-1">
								{{ platform.platformName }}
							</div>
							<div v-if="platform.refreshFailureCount > 0" class="text-caption text-error mb-1">
								{{ platform.refreshFailureCount }} failed attempt(s)
							</div>
						</div>
						<div class="d-flex align-center ga-2 exchange-auth-platform-actions">
							<v-chip color="grey" variant="outlined" size="small" class="pa-4">Not Logged In</v-chip>
							<v-btn
								v-if="canReconnect"
								variant="outlined"
								rounded="pill"
								class="d-inline-block exchange-auth-login-btn"
								:loading="authenticating === platform.platformId"
								@click="reconnect(platform)">
								<v-icon left size="small" color="primary">icon-refresh</v-icon>
								Log in
							</v-btn>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</div>
		<template #actions>
			<PrimaryBtn @click="close">Close</PrimaryBtn>
		</template>
	</BaseDialog>
</template>

<script setup lang="ts">
	import { ref } from "vue";
	import BaseDialog from "@common-ui/components/BaseDialog.vue";
	import type { Platform } from "@/types/ExchangeAuthHealth";

	defineProps<{
		platforms: Platform[];
		canReconnect: boolean;
	}>();

	const emit = defineEmits<{
		(e: "reconnect", platform: Platform): void;
	}>();

	const dialogVisible = defineModel<boolean>("visible", { required: true });
	const authenticating = ref<string | null>(null);

	function close() {
		dialogVisible.value = false;
		authenticating.value = null;
	}

	function reconnect(platform: Platform) {
		authenticating.value = platform.platformId;
		emit("reconnect", platform);
	}

	function resetAuthenticating() {
		authenticating.value = null;
	}

	defineExpose({
		resetAuthenticating
	});
</script>

<style scoped>
	.exchange-auth-login-btn {
		height: 32px;
	}

	.exchange-auth-health-title {
		line-height: 1.3;
		word-break: break-word;
	}

	@media (max-width: 600px) {
		.exchange-auth-health-title {
			line-height: 1.2;
			font-size: 1.1rem;
		}

		.exchange-auth-platform-row {
			flex-direction: column;
			align-items: flex-start !important;
		}

		.exchange-auth-platform-actions {
			width: 100%;
			flex-wrap: wrap;
		}
	}
</style>
